import {UnreachableError} from '../../libs/shared/helpers/src/types/customErrors';
import {ConversationModel} from '../models/api/conversationModel';
import {CountersModel} from '../models/api/countersModel';
import {CustomViewModel} from '../models/api/customViewModel';
import {CountersVisibilityModesEnum} from '../models/api/preferencesModel';
import {GlobalFoldersEnum, InboxesFiltersEnum, TeammatesFoldersEnum} from '../models/url/inboxesUrlModel';
import {ResourceMap} from '../store/appTypes';
import {CellsContext} from '../store/panels/cells/context/cellsContext';
import {
  InboxesConversationsParameters,
  InboxesParametersTypesEnum,
} from '../store/panels/inboxes/parameters/inboxesParameters';
import {
  buildCustomViewCounterKey,
  buildPrivateInboxCounterKey,
  buildSharedInboxCounterKey,
  buildTeamCounterKey,
  buildTeammateAssignedCounterKey,
  discussionsCounterKey,
  findBadgeCounter,
  findBadgeUid,
  selfAssignedCounterKey,
  selfInboxCounterKey,
  sharedWithMeCounterKey,
  unifiedTeammatesCounterKey,
} from './counterHelpers';
import {
  ConversationStatusesEnum,
  findConversationStatus,
  findUnreadStatus,
  UnreadStatusesEnum,
} from './models/conversationHelpers';

/*
 * Constants.
 */

const userFoldersToCounterKeyMap = {
  [TeammatesFoldersEnum.INBOX]: selfInboxCounterKey,
  [TeammatesFoldersEnum.ASSIGNED]: selfAssignedCounterKey,
  [TeammatesFoldersEnum.SHARED_WITH_ME]: sharedWithMeCounterKey,
  [TeammatesFoldersEnum.DISCUSSIONS]: discussionsCounterKey,
} as {[folder: string]: string | undefined};

export function getCounter(
  counters: CountersModel,
  visibility: CountersVisibilityModesEnum,
  parameters: InboxesConversationsParameters,
  customViews: ResourceMap<CustomViewModel>,
) {
  const key = getCounterKey(parameters, visibility, customViews);
  if (!key) {
    return undefined;
  }

  const value = findBadgeCounter(counters, visibility, key);
  const uid = findBadgeUid(counters, visibility, key);

  return {key, value, uid};
}

function getCounterKey(
  parameters: InboxesConversationsParameters,
  visibility: CountersVisibilityModesEnum,
  customViews: ResourceMap<CustomViewModel>,
): string | undefined {
  if (parameters.search) {
    return undefined;
  }

  switch (parameters.type) {
    case InboxesParametersTypesEnum.USER:
      return userFoldersToCounterKeyMap[parameters.folder];

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
      return buildPrivateInboxCounterKey(parameters.inboxId);

    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
    case InboxesParametersTypesEnum.VIEW: {
      const customView = customViews[parameters.viewId];
      return customView ? buildCustomViewCounterKey(customView.uid, customView.type) : undefined;
    }

    // TODO-LENS-COUNTERS: add counters to lenses.
    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAM_LENS:
      return undefined;

    case InboxesParametersTypesEnum.INBOX:
      return buildSharedInboxCounterKey(parameters.inboxId, visibility);

    case InboxesParametersTypesEnum.TEAM:
      return buildTeamCounterKey(parameters.teamId, visibility);

    case InboxesParametersTypesEnum.GLOBAL:
      return parameters.folder === GlobalFoldersEnum.ASSIGNED ? unifiedTeammatesCounterKey : undefined;

    case InboxesParametersTypesEnum.TEAMMATE:
      return buildTeammateAssignedCounterKey(parameters.teammateId);

    default:
      throw new UnreachableError(parameters);
  }
}

export function areFiltersIncludingCounterVisibility(
  filters: ReadonlyArray<InboxesFiltersEnum>,
  counterVisibility: CountersVisibilityModesEnum,
) {
  switch (counterVisibility) {
    case CountersVisibilityModesEnum.OPEN:
    case CountersVisibilityModesEnum.UNREAD:
      return (
        filters.includes(InboxesFiltersEnum.OPEN) ||
        (filters.includes(InboxesFiltersEnum.UNASSIGNED) && filters.includes(InboxesFiltersEnum.ASSIGNED))
      );

    case CountersVisibilityModesEnum.UNASSIGNED:
      return filters.includes(InboxesFiltersEnum.OPEN) || filters.includes(InboxesFiltersEnum.UNASSIGNED);

    default:
      return false;
  }
}

export function isConversationMatchingCounterVisibility(
  conversation: ConversationModel,
  counterVisibility: CountersVisibilityModesEnum,
  cellsContext: CellsContext,
) {
  const status = findConversationStatus(cellsContext, conversation);
  if (status !== ConversationStatusesEnum.OPEN) {
    return false;
  }

  switch (counterVisibility) {
    case CountersVisibilityModesEnum.OPEN:
      return true;

    case CountersVisibilityModesEnum.UNREAD:
      return findUnreadStatus(cellsContext, conversation) !== UnreadStatusesEnum.NONE;

    case CountersVisibilityModesEnum.UNASSIGNED:
      return !conversation.assigneeId;

    default:
      return false;
  }
}

export enum CounterTypesEnum {
  TEAMMATE = 'teammate',
  INBOX = 'inbox',
  TAG = 'tag',
}

export interface CounterContext {
  type: CounterTypesEnum;
  folder?: string;
  tagId?: number;
  inboxId?: number;
}

export function buildCounterContext(
  parameters: InboxesConversationsParameters,
  key: string,
): CounterContext | undefined {
  switch (parameters.type) {
    case InboxesParametersTypesEnum.USER:
      return {type: CounterTypesEnum.TEAMMATE, folder: parameters.folder};

    case InboxesParametersTypesEnum.TEAMMATE_INBOX:
    case InboxesParametersTypesEnum.TEAMMATE:
    case InboxesParametersTypesEnum.TEAM:
      return {type: CounterTypesEnum.TEAMMATE, folder: key};

    case InboxesParametersTypesEnum.VIEW:
    case InboxesParametersTypesEnum.TEAMMATE_VIEW:
      return {type: CounterTypesEnum.TAG, tagId: parameters.viewId};

    // TODO-LENS-COUNTERS: add counters to lenses.
    case InboxesParametersTypesEnum.TEAMMATE_LENS:
    case InboxesParametersTypesEnum.TEAM_LENS:
      return undefined;

    case InboxesParametersTypesEnum.INBOX:
      return {type: CounterTypesEnum.INBOX, inboxId: parameters.inboxId};

    case InboxesParametersTypesEnum.GLOBAL:
      return parameters.folder === GlobalFoldersEnum.ASSIGNED
        ? {type: CounterTypesEnum.TEAMMATE, folder: 'teammates_unified'}
        : undefined;

    default:
      throw new UnreachableError(parameters);
  }
}
